import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Conversation, MessageInterface, ConversationStatus } from '../../../models/chat';
import { Campaign } from '../../../models/campaign';

// PrimeNG Modules
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { TooltipModule } from 'primeng/tooltip';
import { SharedDataService } from '../../../services/shared-data.service';
import { Agent } from '../../../models/users';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

interface CloseReason {
  label: string;
  value: string | null;
}

@Component({
  selector: 'app-chat-title-bar',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DropdownModule,
    TooltipModule,
    TranslateModule
  ],
  templateUrl: './chat-title-bar.component.html',
  styleUrls: ['./chat-title-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatTitleBarComponent implements OnInit, OnChanges, OnDestroy {
  @Input() selectedConversation: Conversation | null = null;
  @Input() isTranslationEnabled: boolean = false;
  @Input() isInfoPanelVisible: boolean = false;
  @Input() closeReasons: CloseReason[] = [];
  @Input() chatMessages: MessageInterface[] = [];
  @Input() customVars: any[] = [];
  @Input() campaign: Campaign | null = null;
  @Input() workspaceId: string = '';
  @Input() platformId: number = 0;

  @Output() translationToggled = new EventEmitter<void>();
  @Output() infoPanelToggled = new EventEmitter<void>();
  @Output() conversationCloseRequested = new EventEmitter<string>(); // Emits the selected reason
  @Output() transferAgentRequested = new EventEmitter<void>();

  get canTransferAgent(): boolean {
  const validWorkspace = typeof this.workspaceId === 'string' && this.workspaceId.trim() !== '';
  const validPlatform = typeof this.platformId === 'number' && this.platformId > 0;
  const isActiveConversation = this.selectedConversation?.status === ConversationStatus.Active;
  const canTransferBetweenAgent =
    !!this.campaign?.agent_config?.enable &&
    !!this.campaign?.agent_config?.transfer_between_agent;

  return validWorkspace && validPlatform && isActiveConversation && canTransferBetweenAgent;
}

  onTransferAgent(): void {
    this.transferAgentRequested.emit();
  }

  selectedCloseReason: string | null = null;

  user: Agent = {} as Agent;


  get isSupervisor(): boolean {
    return this.user?.role === 'supervisor';
  }

  // Timer properties
  elapsedTime: string = '00:00:00';
  private timerInterval: any;

  constructor(private sharedDataService: SharedDataService, private cdr: ChangeDetectorRef, private translate: TranslateService) { }

  ngOnInit(): void {
    if (this.selectedConversation) {
      this.selectedCloseReason = null;
    }

    this.user = this.sharedDataService.getAgent() as Agent;
    this.startTimer();
  }

  ngOnChanges(): void {
    // Restart timer when selectedConversation changes
    this.startTimer();
  }

  ngOnDestroy(): void {
    this.stopTimer();
  }

  private startTimer(): void {
    this.stopTimer(); // Clear any existing timer

    if (this.selectedConversation?.startDate) {
      this.updateElapsedTime();
      this.timerInterval = setInterval(() => {
        this.updateElapsedTime();
        this.cdr.detectChanges(); // Trigger change detection for OnPush strategy
      }, 1000);
    } else {
      this.elapsedTime = '00:00:00';
    }
  }

  private stopTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }
  private updateElapsedTime(): void {
    if (!this.selectedConversation?.startDate) {
      this.elapsedTime = '00:00:00';
      return;
    }

    const startDateUTC = new Date(this.selectedConversation.startDate);

    // Convert current time to UTC-5 (COT)
    const nowUTC = new Date();
    const nowCOT = new Date(nowUTC.getTime() - 5 * 60 * 60 * 1000);

    // Calculate elapsed time in milliseconds using COT current time
    const elapsedMs = nowCOT.getTime() - startDateUTC.getTime();

    if (elapsedMs < 0) {
      this.elapsedTime = '00:00:00';
      return;
    }

    // Convert to seconds, minutes, hours
    const totalSeconds = Math.floor(elapsedMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    // Format as HH:MM:SS
    this.elapsedTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  getCampaignValue(variables: { name: string; value: string }[]): string | null {
    const campaignVar = variables.find(v => v.name === 'Campaña');
    return campaignVar ? campaignVar.value : null;
  }

  copyTranscriptToClipboard(): void {
    if (!this.chatMessages?.length || !this.selectedConversation) {
      return;
    }

    const userFullName = `${this.selectedConversation.customVariables?.['Nombres'] || this.translate.instant('agent.title.userFallback')} ${this.selectedConversation.customVariables?.['Apellidos'] || ''}`.trim();
    const agentName = this.translate.instant('agent.title.agentFallback');

    const lines: string[] = this.chatMessages
      .filter(msg => !!msg.content && (msg.dateSent || msg.datePending))
      .sort((a, b) => {
        const dateA = new Date(a.datePending!).getTime();
        const dateB = new Date(b.datePending!).getTime();
        return dateA - dateB;
      })
      .reduce<string[]>((acc, msg) => {
        // Calculate local time (UTC-5 for COT)
        const localDate = new Date(msg.datePending!);
        const localHours = localDate.getUTCHours();
        const minutes = localDate.getUTCMinutes().toString().padStart(2, '0');

        // Convert to 12-hour format with AM/PM
        const isPM = localHours >= 12;
        const displayHours = localHours === 0 ? 12 : localHours > 12 ? localHours - 12 : localHours;
        const hours = displayHours.toString().padStart(2, '0');
        const ampm = isPM ? 'PM' : 'AM';

        if (this.isContextMessage(msg)) {
          // Si es mensaje de Contexto, lo parseamos con nuestro helper
          acc.push(`[${hours}:${minutes} ${ampm}] ${this.translate.instant('agent.title.contextLabel')}`);
          this.parseContextContent(msg).forEach(field => {
            acc.push(`  ${field.label}: ${field.value}`);
          });
        } else {
          // Mensaje normal: reemplazamos <br> por saltos de línea
          const text = msg.content.replace(/<br\s*\/?>/gi, '\n').trim();
          const sender = msg.type === 'agent' ? agentName : userFullName;
          acc.push(`[${hours}:${minutes} ${ampm}] ${sender}: ${text}`);
        }

        return acc;
      }, []);

    const transcript = lines.join('\n');
    navigator.clipboard.writeText(transcript)
      .then(() => {
        // opcional: mostrar toast o console.log
      })
      .catch(err => {
        console.error('❌ Error al copiar la transcripción:', err);
      });
  }

  public isContextMessage(message: MessageInterface): boolean {
    return !!message.content?.trim().toLowerCase().startsWith('contexto:');
  }

  public parseContextContent(message: MessageInterface): Array<{ label: string; value: string }> {
    const withoutHeader = message.content!
      .replace(/Contexto:\s*<br\s*\/?>/i, '')
      .trim();
    return withoutHeader
      .split(/<br\s*\/?>/i)
      .map(line => line.trim())
      .filter(line => line)
      .map(line => {
        const [key, ...rest] = line.split(':');
        return { label: key.trim(), value: rest.join(':').trim() };
      });
  }

  onToggleTranslation(): void {
    this.translationToggled.emit();
  }

  onToggleInfoPanel(): void {
    this.infoPanelToggled.emit();
  }

  onCloseReasonChange(event: any): void {
    if (event.value) {
      this.conversationCloseRequested.emit(event.value);
    }
  }
}
